import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideDown = keyframes`
  from { opacity: 0; max-height: 0; }
  to { opacity: 1; max-height: 500px; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const fadeOverlay = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Layout
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafe 0%, #f0f4f8 50%, #e8f0f2 100%);
  padding: 32px 24px;

  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const Container = styled.div`
  max-width: 1480px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

// Header
const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  h1 {
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, #1a1a2e, #16213e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.5px;
    margin: 0 0 6px 0;
  }

  p {
    color: #7c8db0;
    font-size: 0.95rem;
    margin: 0;
    font-weight: 400;
  }
`;

const ExportPopulationButton = styled.button`
  background: linear-gradient(135deg, #5B8A72, #4a7a65);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(91, 138, 114, 0.3);
  letter-spacing: 0.2px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(91, 138, 114, 0.4);
    background: linear-gradient(135deg, #6a9d83, #5B8A72);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Stats
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-out;
  animation-fill-mode: backwards;
  animation-delay: ${props => props.delay || '0s'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.accent || 'linear-gradient(90deg, #5B8A72, #7ab89a)'};
    border-radius: 3px 3px 0 0;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
`;

const StatLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 10px;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
`;

// View Toggle
const ViewToggle = styled.div`
  display: flex;
  background: white;
  border-radius: 14px;
  padding: 5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
  width: fit-content;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const ToggleButton = styled.button`
  padding: 12px 28px;
  background: ${props => props.active ? 'linear-gradient(135deg, #5B8A72, #4a7a65)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#64748b'};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  position: relative;

  ${props => props.active && `
    box-shadow: 0 4px 12px rgba(91, 138, 114, 0.3);
  `}

  &:hover:not([disabled]) {
    ${props => !props.active && `
      background: #f1f5f9;
      color: #334155;
    `}
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Filters
const FiltersContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: center;
  animation: ${slideDown} 0.3s ease-out;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }
`;

const StyledSelect = styled.select`
  padding: 10px 36px 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #334155;
  background: #f8fafc;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: #5B8A72;
    box-shadow: 0 0 0 3px rgba(91, 138, 114, 0.15);
    background-color: white;
  }
`;

const StyledInput = styled.input`
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #334155;
  background: #f8fafc;
  transition: all 0.2s ease;
  min-width: 140px;

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: #5B8A72;
    box-shadow: 0 0 0 3px rgba(91, 138, 114, 0.15);
    background-color: white;
  }
`;

const ClearFiltersButton = styled.button`
  padding: 10px 18px;
  background: #fef2f2;
  color: #ef4444;
  border: 2px solid #fecaca;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-end;

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Table Card
const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  animation: ${fadeIn} 0.4s ease-out;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th {
    padding: 16px 18px;
    text-align: left;
    font-weight: 700;
    color: #64748b;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  td {
    padding: 16px 18px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.9rem;
    color: #334155;
    vertical-align: middle;
  }

  tbody tr {
    transition: all 0.2s ease;

    &:hover {
      background: linear-gradient(90deg, #f0fdf4, #f8fafc);
    }

    &:last-child td {
      border-bottom: none;
    }
  }
`;

// Client Cell
const ClientCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${props => props.color1 || '#5B8A72'}, ${props => props.color2 || '#7ab89a'});
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 0.8rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px ${props => props.color1 || '#5B8A72'}40;
`;

const ClientInfo = styled.div`
  .name {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
    line-height: 1.3;
  }
`;

// Chips & Badges
const ScorePillContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
`;

const ScorePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${props => props.bg || '#f1f5f9'};
  color: ${props => props.color || '#64748b'};
  margin-bottom: 3px;
  letter-spacing: 0.3px;
  border: 1px solid ${props => props.borderColor || 'transparent'};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  background: ${props => props.paid ? '#f0fdf4' : '#fffbeb'};
  color: ${props => props.paid ? '#16a34a' : '#d97706'};
  border: 1px solid ${props => props.paid ? '#bbf7d0' : '#fde68a'};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.paid ? '#16a34a' : '#d97706'};
  }
`;


const DemographicsList = styled.div`
  display: grid;
  gap: 4px;
  min-width: 270px;

  .demo-row {
    display: grid;
    grid-template-columns: 82px minmax(0, 1fr);
    gap: 8px;
  }

  .demo-label {
    color: #94a3b8;
    font-size: 0.66rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .demo-value {
    color: #334155;
    font-size: 0.76rem;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const TestBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #ede9fe;
  color: #7c3aed;
  border: 1px solid #ddd6fe;
`;

const PractitionerName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${props => props.isAdmin ? '#94a3b8' : '#334155'};
  font-style: ${props => props.isAdmin ? 'italic' : 'normal'};
  font-weight: ${props => props.isAdmin ? '400' : '500'};
`;

const DateDisplay = styled.span`
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
`;


const RiskBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  background: ${props => props.level === 'critical' ? '#fee2e2' : props.level === 'high' ? '#fff7ed' : props.level === 'moderate' ? '#fefce8' : '#f1f5f9'};
  color: ${props => props.level === 'critical' ? '#991b1b' : props.level === 'high' ? '#c2410c' : props.level === 'moderate' ? '#a16207' : '#64748b'};
  border: 1px solid ${props => props.level === 'critical' ? '#fecaca' : props.level === 'high' ? '#fed7aa' : props.level === 'moderate' ? '#fef08a' : '#e2e8f0'};
  text-transform: capitalize;
  white-space: nowrap;
`;

const ViewDetailsButton = styled.button`
  padding: 8px 14px;
  background: linear-gradient(135deg, #5B8A72, #4a7a65);
  color: white;
  border: none;
  border-radius: 9px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 14px rgba(91, 138, 114, 0.28);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
  animation: ${fadeOverlay} 0.2s ease;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 22px;
  width: min(860px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
  animation: ${slideUp} 0.28s ease;

  @media (max-width: 640px) {
    padding: 20px;
    border-radius: 18px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

const ModalTitle = styled.div`
  h2 { margin: 0 0 6px; color: #0f172a; font-size: 1.25rem; }
  p { margin: 0; color: #64748b; font-size: 0.9rem; }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #f8fafc; color: #0f172a; }
  svg { width: 16px; height: 16px; }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  background: linear-gradient(135deg, #f0fdf4, #f8fafc);
  border: 1px solid #d9f99d;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 18px;
`;

const DetailItem = styled.div`
  .label { color: #64748b; font-size: 0.68rem; font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 4px; }
  .value { color: #1e293b; font-size: 0.9rem; font-weight: 700; }
`;

const SectionTitle = styled.h3`
  margin: 22px 0 12px;
  color: #1e293b;
  font-size: 1rem;
`;

const ScoreCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
`;

const ScoreCard = styled.div`
  border: 1px solid ${props => props.borderColor || '#e2e8f0'};
  background: ${props => props.bg || '#f8fafc'};
  border-radius: 14px;
  padding: 14px;
  .name { color: #64748b; font-size: 0.72rem; font-weight: 800; text-transform: uppercase; margin-bottom: 8px; }
  .score { color: ${props => props.color || '#334155'}; font-size: 1.8rem; font-weight: 900; line-height: 1; margin-bottom: 8px; }
  .severity { color: ${props => props.color || '#334155'}; font-size: 0.78rem; font-weight: 800; }
`;

const AlertBox = styled.div`
  margin-bottom: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${props => props.level === 'critical' ? '#fef2f2' : '#fff7ed'};
  border-left: 4px solid ${props => props.level === 'critical' ? '#dc2626' : '#ea580c'};
  color: ${props => props.level === 'critical' ? '#7f1d1d' : '#9a3412'};
  font-size: 0.88rem;
  line-height: 1.5;
`;

const ResponsesSection = styled.div`
  margin-top: 18px;
`;

const ResponseRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr minmax(70px, auto);
  gap: 12px;
  padding: 11px 12px;
  border-radius: 10px;
  background: ${props => props.idx % 2 ? '#ffffff' : '#f8fafc'};
  border: 1px solid #f1f5f9;
  margin-bottom: 6px;

  .num { color: #5B8A72; font-weight: 800; }
  .text { color: #334155; line-height: 1.45; }
  .value { color: #166534; font-weight: 800; text-align: right; }
`;


// Pagination
const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-top: 1px solid #f1f5f9;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageInfo = styled.span`
  font-size: 0.85rem;
  color: #94a3b8;
  font-weight: 500;
`;

const PageButtons = styled.div`
  display: flex;
  gap: 6px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 2px solid ${props => props.active ? '#5B8A72' : '#e2e8f0'};
  background: ${props => props.active ? 'linear-gradient(135deg, #5B8A72, #4a7a65)' : 'white'};
  color: ${props => props.active ? 'white' : '#64748b'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #5B8A72;
    color: ${props => props.active ? 'white' : '#5B8A72'};
    background: ${props => props.active ? 'linear-gradient(135deg, #6a9d83, #5B8A72)' : '#f0fdf4'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Loading
const LoadingSkeleton = styled.div`
  padding: 0;

  .row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 24px;
    border-bottom: 1px solid #f1f5f9;
  }

  .bone {
    height: ${props => props.h || '14px'};
    width: ${props => props.w || '100px'};
    border-radius: 6px;
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 2000px 100%;
    animation: ${shimmer} 2s infinite linear;
  }
`;

const Bone = styled.div`
  height: ${props => props.h || '14px'};
  width: ${props => props.w || '100px'};
  border-radius: ${props => props.r || '6px'};
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 2000px 100%;
  animation: ${shimmer} 2s infinite linear;
`;

// Empty State
const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 80px 32px;
  animation: ${fadeIn} 0.5s ease-out;

  .icon {
    width: 80px;
    height: 80px;
    border-radius: 24px;
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    font-size: 2rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
  }

  p {
    color: #94a3b8;
    font-size: 0.95rem;
    margin: 0;
    max-width: 420px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

// Population View
const PopulationCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  animation: ${fadeIn} 0.4s ease-out;
`;

const PopulationToolbar = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc, #f0f9f4);
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const RespondentCount = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #475569;

  strong {
    background: linear-gradient(135deg, #5B8A72, #4a7a65);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-size: 1.1rem;
  }
`;

const ExportButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ExportBtn = styled.button`
  padding: 9px 18px;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.2px;

  background: ${props => {
    if (props.variant === 'csv') return 'linear-gradient(135deg, #5B8A72, #4a7a65)';
    if (props.variant === 'stata') return 'linear-gradient(135deg, #2563eb, #1d4ed8)';
    if (props.variant === 'spss') return 'linear-gradient(135deg, #ea580c, #c2410c)';
    return '#5B8A72';
  }};

  box-shadow: ${props => {
    if (props.variant === 'csv') return '0 3px 10px rgba(91, 138, 114, 0.3)';
    if (props.variant === 'stata') return '0 3px 10px rgba(37, 99, 235, 0.3)';
    if (props.variant === 'spss') return '0 3px 10px rgba(234, 88, 12, 0.3)';
    return 'none';
  }};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => {
      if (props.variant === 'csv') return '0 6px 18px rgba(91, 138, 114, 0.4)';
      if (props.variant === 'stata') return '0 6px 18px rgba(37, 99, 235, 0.4)';
      if (props.variant === 'spss') return '0 6px 18px rgba(234, 88, 12, 0.4)';
      return 'none';
    }};
  }

  &:active {
    transform: translateY(0);
  }
`;

const PopulationTableScroll = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  max-height: 700px;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const PopTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th {
    padding: 14px 16px;
    text-align: left;
    font-weight: 700;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.88rem;
    color: #334155;
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: #fafbfc;
    }

    &:last-child td {
      border-bottom: none;
    }
  }
`;

const StickyCell = styled.td`
  position: sticky;
  left: 0;
  z-index: 4;
  background: white;
  font-weight: 600;

  tr:hover & {
    background: #fafbfc;
  }
`;

const StickyHeader = styled.th`
  position: sticky;
  left: 0;
  z-index: 11 !important;
  background: #f8fafc !important;
  min-width: 160px;
`;

const QuestionHeader = styled.th`
  min-width: 240px;
  text-align: center !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  padding: 10px 8px !important;
  line-height: 1.4;
`;

const ResponseCode = styled.code`
  display: inline-block;
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  font-size: 0.82rem;
  color: #475569;
  font-weight: 600;
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const PopulationFooter = styled.div`
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 0.82rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    color: #64748b;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

// Helper Functions
const getAvatarColors = (name) => {
  const colors = [
    ['#6366f1', '#818cf8'],
    ['#ec4899', '#f472b6'],
    ['#f59e0b', '#fbbf24'],
    ['#10b981', '#34d399'],
    ['#3b82f6', '#60a5fa'],
    ['#8b5cf6', '#a78bfa'],
    ['#ef4444', '#f87171'],
    ['#14b8a6', '#2dd4bf'],
  ];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const getInitials = (firstName, lastName) => {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
};

// Icons
const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
  </svg>
);

// Component
const Results = () => {
  const [results, setResults] = useState([]);
  const [tests, setTests] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('individual');
  const [populationTestFilter, setPopulationTestFilter] = useState('');
  const [populationResults, setPopulationResults] = useState([]);
  const [populationLoading, setPopulationLoading] = useState(false);
  const [orgNameFilter, setOrgNameFilter] = useState('');
  const [resultScope, setResultScope] = useState('all');
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState({
    testId: '',
    practitionerId: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadFiltersData();
  }, []);

  useEffect(() => {
    loadResults();
  }, [page, filters]);

  const loadFiltersData = async () => {
    try {
      const [testsRes, practitionersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/tests`),
        axios.get(`${API_URL}/admin/practitioners?status=approved&limit=100`)
      ]);
      setTests(testsRes.data);
      setPractitioners(practitionersRes.data.practitioners);
    } catch (error) {
      console.error('Failed to load filter data:', error);
    }
  };

  const loadResults = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 25, ...filters };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await axios.get(`${API_URL}/admin/results`, { params });
      setResults(response.data.results);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const params = { ...filters, format };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);

      if (format === 'csv') {
        const response = await axios.get(`${API_URL}/admin/results/export`, { params, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `test_results_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export downloaded!');
      } else {
        const response = await axios.get(`${API_URL}/admin/results/export`, { params });
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `test_results_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export downloaded!');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getSeverityColor = (severity) => {
    const map = {
      'Normal': { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
      'Mild': { color: '#65a30d', bg: '#f7fee7', border: '#d9f99d' },
      'Moderate': { color: '#ca8a04', bg: '#fefce8', border: '#fef08a' },
      'Severe': { color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
      'Extremely Severe': { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
    };
    return map[severity] || { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' };
  };

  const loadPopulationResults = async (testId) => {
    if (!testId) {
      setPopulationResults([]);
      return;
    }
    try {
      setPopulationLoading(true);
      const response = await axios.get(`${API_URL}/admin/results`, { params: { testId, limit: 1000 } });
      setPopulationResults(response.data.results);
    } catch (error) {
      console.error('Failed to load population results:', error);
      toast.error('Failed to load population data');
      setPopulationResults([]);
    } finally {
      setPopulationLoading(false);
    }
  };

  const getOtherText = (valueObject) => {
    if (!valueObject || typeof valueObject !== 'object') return '';
    const candidates = [
      valueObject.other,
      valueObject.otherValue,
      valueObject.otherText,
      valueObject.customValue,
      valueObject.specify,
      valueObject.text,
      valueObject.label,
    ];
    const resolved = candidates.find((v) => typeof v === 'string' && v.trim());
    return resolved ? resolved.trim() : '';
  };

  const formatResponseValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      const optionValue = 'value' in value ? value.value : value;
      const optionText = String(optionValue ?? '').trim();
      if (optionText.toLowerCase() === 'other') {
        return getOtherText(value) || optionText;
      }
      return optionText || getOtherText(value);
    }
    return String(value);
  };

  const getResponseValue = (responses, questionId) => {
    if (!responses) return '';
    if (Array.isArray(responses)) {
      const found = responses.find(r => r.questionId === questionId);
      return found ? formatResponseValue(found.value) : '';
    }
    const value = responses[questionId];
    if (value && typeof value === 'object' && 'value' in value) {
      return formatResponseValue(value.value);
    }
    return formatResponseValue(value ?? '');
  };


  const NULL_DISPLAY = 'Null';

  const formatDemographicValue = (value) => {
    if (value === null || value === undefined || value === '') return NULL_DISPLAY;
    const text = String(value).trim();
    return text || NULL_DISPLAY;
  };

  const getClientGenderDisplay = (client) => {
    if (!client) return NULL_DISPLAY;
    const gender = String(client.gender || '').trim();
    const custom = String(client.genderCustom || '').trim();
    if (gender.toLowerCase() === 'other' && custom) return custom;
    return custom || gender || NULL_DISPLAY;
  };

  const getClientDemographics = (client) => [
    ['Age', formatDemographicValue(client?.age)],
    ['Gender', getClientGenderDisplay(client)],
    ['Email', formatDemographicValue(client?.email)],
    ['Phone', formatDemographicValue(client?.phone)],
    ['Occupation', formatDemographicValue(client?.occupation)],
    ['Education', formatDemographicValue(client?.education)],
    ['Marital', formatDemographicValue(client?.maritalStatus)]
  ];

  const renderDemographicsList = (client) => (
    <DemographicsList>
      {getClientDemographics(client).map(([label, value]) => (
        <div className="demo-row" key={label}>
          <span className="demo-label">{label}</span>
          <span className="demo-value">{value}</span>
        </div>
      ))}
    </DemographicsList>
  );

  const getRiskLabel = (risk) => {
    if (!risk) return 'low';
    return risk.level || 'low';
  };

  const viewResult = async (resultId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API_URL}/admin/results/${resultId}`);
      setSelectedResult(response.data);
    } catch (error) {
      console.error('Failed to load result details:', error);
      toast.error('Failed to load result details');
    } finally {
      setDetailLoading(false);
    }
  };

  const renderResultDetails = () => {
    if (!selectedResult) return null;
    const scoreEntries = selectedResult.interpretation
      ? Object.entries(selectedResult.interpretation).filter(([key, val]) =>
        key && !key.startsWith('_') && val && typeof val === 'object' && 'severity' in val && 'score' in val
      )
      : [];
    const questions = selectedResult.TestCatalog?.questions || [];
    const risk = selectedResult.followUpRisk || { level: 'low', reasons: [], recommendedAction: 'No automated follow-up flag' };

    return (
      <Overlay onClick={() => setSelectedResult(null)}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              <h2>{selectedResult.TestCatalog?.name || 'Assessment'} — Results</h2>
              <p>Admin view mirrors practitioner result details with follow-up risk flagging.</p>
            </ModalTitle>
            <CloseButton onClick={() => setSelectedResult(null)}><XIcon /></CloseButton>
          </ModalHeader>

          <DetailGrid>
            <DetailItem><div className="label">Client</div><div className="value">{selectedResult.Client?.firstName} {selectedResult.Client?.lastName}</div></DetailItem>
            <DetailItem><div className="label">Age</div><div className="value">{formatDemographicValue(selectedResult.Client?.age)}</div></DetailItem>
            <DetailItem><div className="label">Gender</div><div className="value">{getClientGenderDisplay(selectedResult.Client)}</div></DetailItem>
            <DetailItem><div className="label">Email</div><div className="value">{formatDemographicValue(selectedResult.Client?.email)}</div></DetailItem>
            <DetailItem><div className="label">Phone</div><div className="value">{formatDemographicValue(selectedResult.Client?.phone)}</div></DetailItem>
            <DetailItem><div className="label">Occupation</div><div className="value">{formatDemographicValue(selectedResult.Client?.occupation)}</div></DetailItem>
            <DetailItem><div className="label">Education</div><div className="value">{formatDemographicValue(selectedResult.Client?.education)}</div></DetailItem>
            <DetailItem><div className="label">Marital Status</div><div className="value">{formatDemographicValue(selectedResult.Client?.maritalStatus)}</div></DetailItem>
            <DetailItem><div className="label">Practitioner</div><div className="value">{selectedResult.Practitioner ? `${selectedResult.Practitioner.firstName} ${selectedResult.Practitioner.lastName}` : 'Admin'}</div></DetailItem>
            <DetailItem><div className="label">Completed</div><div className="value">{new Date(selectedResult.completedAt).toLocaleDateString()}</div></DetailItem>
            <DetailItem><div className="label">Follow-up</div><div className="value"><RiskBadge level={getRiskLabel(risk)}>{getRiskLabel(risk)}</RiskBadge></div></DetailItem>
          </DetailGrid>

          {risk.isFlagged && (
            <AlertBox level={risk.level}>
              <strong>{risk.recommendedAction}.</strong>
              {risk.reasons?.length > 0 && (
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {risk.reasons.map((reason, idx) => <li key={idx}>{reason}</li>)}
                </ul>
              )}
            </AlertBox>
          )}

          {scoreEntries.length > 0 && (
            <>
              <SectionTitle>Scores</SectionTitle>
              <ScoreCards>
                {scoreEntries.map(([key, val]) => {
                  const sc = getSeverityColor(val.severity);
                  return (
                    <ScoreCard key={key} bg={sc.bg} color={sc.color} borderColor={sc.border}>
                      <div className="name">{key.replace(/_/g, ' ')}</div>
                      <div className="score">{val.score}</div>
                      <div className="severity">{val.severity}</div>
                    </ScoreCard>
                  );
                })}
              </ScoreCards>
            </>
          )}

          {selectedResult.interpretation?._description && (
            <AlertBox level="info" style={{ background: '#f0f9ff', borderLeftColor: '#0284c7', color: '#0c4a6e', marginTop: 18 }}>
              <strong>Test Description:</strong> {selectedResult.interpretation._description}
            </AlertBox>
          )}

          {selectedResult.interpretation?._recommendations && (
            <AlertBox level="info" style={{ background: '#f5f3ff', borderLeftColor: '#7c3aed', color: '#5b21b6' }}>
              <strong>Recommendations:</strong> {selectedResult.interpretation._recommendations}
            </AlertBox>
          )}

          {selectedResult.alerts?.length > 0 && (
            <>
              <SectionTitle>Clinical Alerts</SectionTitle>
              {selectedResult.alerts.map((alert, idx) => (
                <AlertBox key={idx} level={alert.type === 'CRITICAL' ? 'critical' : 'high'}>{alert.message}</AlertBox>
              ))}
            </>
          )}

          <ResponsesSection>
            <SectionTitle>Individual Responses</SectionTitle>
            {questions.length > 0 ? questions.map((q, idx) => (
              <ResponseRow key={q.id || idx} idx={idx}>
                <span className="num">{idx + 1}.</span>
                <span className="text">{q.text}</span>
                <span className="value">{getResponseValue(selectedResult.responses, q.id) || '–'}</span>
              </ResponseRow>
            )) : (
              <EmptyStateContainer><div className="icon">📝</div><h3>No question data</h3><p>This test does not have question metadata available.</p></EmptyStateContainer>
            )}
          </ResponsesSection>
        </ModalContainer>
      </Overlay>
    );
  };

  const directResults = results.filter(r => r.TestAssignment?.linkType !== 'organization');
  const orgResults = results.filter(r => r.TestAssignment?.linkType === 'organization');
  const orgNameOptions = Array.from(
    new Set(orgResults
      .map(r => (r.TestAssignment?.organizationName || '').trim())
      .filter(Boolean))
  ).sort();

  const filteredOrgResults = orgNameFilter
    ? orgResults.filter(r => (r.TestAssignment?.organizationName || '').toLowerCase().includes(orgNameFilter.toLowerCase().trim()))
    : orgResults;

  const renderResultsTable = (data, title) => (
    <>
      <h3 style={{ marginTop: '24px', marginBottom: '12px', color: '#1e293b' }}>{title}</h3>
      <TableCard>
        {loading ? (
          renderSkeletonRows()
        ) : data.length === 0 ? (
          <EmptyStateContainer>
            <div className="icon">📋</div>
            <h3>No results found</h3>
            <p>
              {title === 'Organizational Link Results'
                ? 'No organization-link results are available for this filter.'
                : 'No results are available for this filter.'}
            </p>
          </EmptyStateContainer>
        ) : (
          <TableWrapper>
            <DataTable>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Demographics</th>
                  <th>Test</th>
                  <th>Practitioner</th>
                  <th>Scores</th>
                  <th>Follow-up</th>
                  <th>Date</th>
                  <th>Payment</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map(result => {
                  const colors = getAvatarColors(result.Client?.firstName);
                  return (
                    <tr key={result.id}>
                      <td>
                        <ClientCell>
                          <Avatar color1={colors[0]} color2={colors[1]}>
                            {getInitials(result.Client?.firstName, result.Client?.lastName)}
                          </Avatar>
                          <ClientInfo>
                            <div className="name">
                              {result.Client?.firstName} {result.Client?.lastName}
                            </div>
                          </ClientInfo>
                        </ClientCell>
                      </td>
                      <td>{renderDemographicsList(result.Client)}</td>
                      <td>
                        <TestBadge>{result.TestCatalog?.shortName}</TestBadge>
                      </td>
                      <td>
                        <PractitionerName isAdmin={!result.Practitioner}>
                          {result.Practitioner
                            ? `${result.Practitioner.firstName} ${result.Practitioner.lastName}`
                            : 'Admin'}
                        </PractitionerName>
                      </td>
                      <td>
                        <ScorePillContainer>
                          {result.interpretation && Object.entries(result.interpretation)
                            .filter(([key, val]) =>
                              key && !key.startsWith('_') &&
                              val && typeof val === 'object' &&
                              'severity' in val &&
                              'score' in val &&
                              val.severity !== undefined
                            )
                            .map(([key, val]) => {
                              const sc = getSeverityColor(val.severity);
                              return (
                                <ScorePill
                                  key={key}
                                  bg={sc.bg}
                                  color={sc.color}
                                  borderColor={sc.border}
                                >
                                  {key.charAt(0).toUpperCase()}: {val.score}
                                </ScorePill>
                              );
                            })}
                        </ScorePillContainer>
                      </td>
                      <td>
                        <RiskBadge level={getRiskLabel(result.followUpRisk)}>
                          {getRiskLabel(result.followUpRisk)}
                        </RiskBadge>
                      </td>
                      <td>
                        <DateDisplay>
                          {new Date(result.completedAt).toLocaleDateString()}
                        </DateDisplay>
                      </td>
                      <td>
                        <StatusBadge paid={result.isPaid}>
                          {result.isPaid ? 'Paid' : 'Pending'}
                        </StatusBadge>
                      </td>
                      <td>
                        {result.TestAssignment?.linkType === 'organization'
                          ? result.TestAssignment.organizationName || 'Organization'
                          : 'Direct'}
                      </td>
                      <td>
                        <ViewDetailsButton onClick={() => viewResult(result.id)} disabled={detailLoading}>
                          View Details
                        </ViewDetailsButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          </TableWrapper>
        )}
      </TableCard>
    </>
  );

  const exportRawData = (format) => {
    if (populationResults.length === 0) {
      toast.error('No data to export');
      return;
    }
    const selectedTest = tests.find(t => t.id === populationTestFilter);
    if (!selectedTest) return;

    const questions = selectedTest.questions || [];
    const headers = ['ResponseID', 'ClientName', 'ClientEmail', 'Age', 'Gender', 'DateCompleted', ...questions.map(q => q.text)];
    const rows = populationResults.map((result) => {
      const baseRow = [
        result.id,
        `${result.Client?.firstName || ''} ${result.Client?.lastName || ''}`,
        result.Client?.email || '',
        result.Client?.age || '',
        result.Client?.genderCustom || result.Client?.gender || '',
        new Date(result.completedAt).toISOString().split('T')[0]
      ];
      const responses = questions.map(q => getResponseValue(result.responses, q.id));
      return [...baseRow, ...responses];
    });

    const downloadFile = (content, type, extension) => {
      const blob = new Blob([content], { type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedTest.shortName}_RawData_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
    };

    if (format === 'csv') {
      const csvContent = [
        headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      downloadFile(csvContent, 'text/csv;charset=utf-8;', 'csv');
      toast.success('Data exported to CSV');
    } else if (format === 'stata') {
      const tsvContent = [headers.join('\t'), ...rows.map(row => row.map(cell => String(cell).replace(/\t/g, ' ')).join('\t'))].join('\n');
      downloadFile(tsvContent, 'text/plain;charset=utf-8;', 'tsv');
      toast.success('Data exported for STATA (TSV format - import with: import delimited)');
    } else if (format === 'spss') {
      const spssContent = [headers.join('\t'), ...rows.map(row => row.map(cell => String(cell).replace(/\t/g, ' ')).join('\t'))].join('\n');
      downloadFile(spssContent, 'text/plain;charset=utf-8;', 'txt');
      toast.success('Data exported for SPSS (TXT format - import with: File > Read Text Data)');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  const renderSkeletonRows = () => (
    <LoadingSkeleton>
      {Array.from({ length: 8 }).map((_, i) => (
        <div className="row" key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Bone w="38px" h="38px" r="10px" />
            <Bone w="120px" h="14px" />
          </div>
          <Bone w="60px" h="14px" />
          <Bone w="80px" h="14px" />
          <Bone w="100px" h="14px" />
          <Bone w="140px" h="14px" />
          <Bone w="70px" h="14px" />
          <Bone w="60px" h="24px" r="20px" />
        </div>
      ))}
    </LoadingSkeleton>
  );

  return (
    <PageWrapper>
      <Container>
        {/* Header */}
        <Header>
          <TitleBlock>
            <h1>Test Results</h1>
            <p>View, analyze, and export all assessment outcomes</p>
          </TitleBlock>
          <ExportPopulationButton onClick={() => setViewMode('population')}>
            <ChartIcon />
            Export Population Data
          </ExportPopulationButton>
        </Header>

        {/* Stats */}
        <StatsGrid>
          <StatCard delay="0.1s" accent="linear-gradient(90deg, #5B8A72, #7ab89a)">
            <StatLabel>
              <span>📊</span> Total Results
            </StatLabel>
            <StatValue>{total.toLocaleString()}</StatValue>
          </StatCard>
        </StatsGrid>

        {/* View Toggle */}
        <ViewToggle>
          <ToggleButton
            active={viewMode === 'individual'}
            onClick={() => setViewMode('individual')}
          >
            <ListIcon />
            Individual Results
          </ToggleButton>
          <ToggleButton
            active={viewMode === 'population'}
            onClick={() => setViewMode('population')}
          >
            <GridIcon />
            Population Data
          </ToggleButton>
        </ViewToggle>

        {/* Individual View */}
        {viewMode === 'individual' ? (
          <>
            <FiltersContainer>
              <FilterGroup>
                <label>Test</label>
                <StyledSelect
                  value={filters.testId}
                  onChange={(e) => handleFilterChange('testId', e.target.value)}
                >
                  <option value="">All Tests</option>
                  {tests.map(test => (
                    <option key={test.id} value={test.id}>{test.shortName}</option>
                  ))}
                </StyledSelect>
              </FilterGroup>

              <FilterGroup>
                <label>Practitioner</label>
                <StyledSelect
                  value={filters.practitionerId}
                  onChange={(e) => handleFilterChange('practitionerId', e.target.value)}
                >
                  <option value="">All Practitioners</option>
                  {practitioners.map(p => (
                    <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                  ))}
                </StyledSelect>
              </FilterGroup>

              <FilterGroup>
                <label>From</label>
                <StyledInput
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <label>To</label>
                <StyledInput
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </FilterGroup>

              {hasActiveFilters && (
                <ClearFiltersButton
                  onClick={() => setFilters({ testId: '', practitionerId: '', startDate: '', endDate: '' })}
                >
                  <XIcon />
                  Clear
                </ClearFiltersButton>
              )}
            </FiltersContainer>

            <FiltersContainer style={{ marginBottom: '16px' }}>
              <FilterGroup>
                <label>Result Scope</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ToggleButton
                    active={resultScope === 'all'}
                    onClick={() => setResultScope('all')}
                  >All</ToggleButton>
                  <ToggleButton
                    active={resultScope === 'direct'}
                    onClick={() => setResultScope('direct')}
                  >Direct</ToggleButton>
                  <ToggleButton
                    active={resultScope === 'org'}
                    onClick={() => setResultScope('org')}
                  >Org</ToggleButton>
                </div>
              </FilterGroup>
              {resultScope !== 'direct' && (
                <FilterGroup>
                  <label>Organization Name</label>
                  <StyledInput
                    value={orgNameFilter}
                    onChange={(e) => setOrgNameFilter(e.target.value)}
                    placeholder="Search organization name..."
                  />
                </FilterGroup>
              )}
            </FiltersContainer>

            {resultScope !== 'org' && renderResultsTable(directResults, 'Direct Results')}
            {(resultScope === 'all' || resultScope === 'org') && orgResults.length > 0 && renderResultsTable(filteredOrgResults, 'Organizational Link Results')}

            {totalPages > 1 && (
              <PaginationBar>
                <PageInfo>
                  Showing page {page} of {totalPages} ({total.toLocaleString()} total)
                </PageInfo>
                <PageButtons>
                  <PageButton
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    ← Previous
                  </PageButton>
                  <PageButton active>{page}</PageButton>
                  <PageButton
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next →
                  </PageButton>
                </PageButtons>
              </PaginationBar>
            )}
          </>
        ) : (
          /* Population View */
          <>
            <FiltersContainer>
              <FilterGroup>
                <label>Select Assessment</label>
                <StyledSelect
                  value={populationTestFilter}
                  onChange={(e) => {
                    setPopulationTestFilter(e.target.value);
                    loadPopulationResults(e.target.value);
                  }}
                  style={{ minWidth: 280 }}
                >
                  <option value="">Choose a test to view data...</option>
                  {tests.map(test => (
                    <option key={test.id} value={test.id}>{test.name}</option>
                  ))}
                </StyledSelect>
              </FilterGroup>
            </FiltersContainer>

            {populationTestFilter ? (
              <PopulationCard>
                {populationLoading ? (
                  <EmptyStateContainer>
                    <div className="icon" style={{ animation: `${pulse} 1.5s infinite` }}>📊</div>
                    <h3>Loading Data…</h3>
                    <p>Fetching population results for analysis.</p>
                  </EmptyStateContainer>
                ) : populationResults.length === 0 ? (
                  <EmptyStateContainer>
                    <div className="icon">📭</div>
                    <h3>No Results Found</h3>
                    <p>No test results available for this assessment yet.</p>
                  </EmptyStateContainer>
                ) : (
                  <>
                    <PopulationToolbar>
                      <RespondentCount>
                        <strong>{populationResults.length}</strong>
                        <span>respondents • Export for statistical analysis</span>
                      </RespondentCount>
                      <ExportButtons>
                        <ExportBtn variant="csv" onClick={() => exportRawData('csv')} title="CSV format for Excel, R, Python, etc.">
                          📊 CSV
                        </ExportBtn>
                        <ExportBtn variant="stata" onClick={() => exportRawData('stata')} title="STATA format (.tsv for import delimited)">
                          📈 STATA
                        </ExportBtn>
                        <ExportBtn variant="spss" onClick={() => exportRawData('spss')} title="SPSS format (text for import)">
                          🔍 SPSS
                        </ExportBtn>
                      </ExportButtons>
                    </PopulationToolbar>

                    <PopulationTableScroll>
                      <PopTable>
                        <thead>
                          <tr>
                            <StickyHeader>Client</StickyHeader>
                            <th style={{ minWidth: 80 }}>Age</th>
                            <th style={{ minWidth: 90 }}>Gender</th>
                            <th style={{ minWidth: 120 }}>Completed</th>
                            {tests.find(t => t.id === populationTestFilter)?.questions?.map((q) => (
                              <QuestionHeader key={q.id}>{q.text}</QuestionHeader>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {populationResults.map(result => (
                            <tr key={result.id}>
                              <StickyCell>
                                {result.Client?.firstName} {result.Client?.lastName}
                              </StickyCell>
                              <td>{result.Client?.age || '–'}</td>
                              <td>{result.Client?.genderCustom || result.Client?.gender || '–'}</td>
                              <td>
                                <DateDisplay>
                                  {new Date(result.completedAt).toLocaleDateString()}
                                </DateDisplay>
                              </td>
                              {tests.find(t => t.id === populationTestFilter)?.questions?.map(q => (
                                <td key={q.id} style={{ textAlign: 'center' }}>
                                  <ResponseCode>
                                    {getResponseValue(result.responses, q.id) || '–'}
                                  </ResponseCode>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </PopTable>
                    </PopulationTableScroll>

                    <PopulationFooter>
                      <InfoIcon />
                      <span>
                        <strong>Data Matrix:</strong> Each row represents one respondent with raw responses per assessment item. Export to CSV, STATA, or SPSS for analysis.
                      </span>
                    </PopulationFooter>
                  </>
                )}
              </PopulationCard>
            ) : (
              <TableCard>
                <EmptyStateContainer>
                  <div className="icon">📊</div>
                  <h3>Population Data View</h3>
                  <p>
                    Select a test from the dropdown above to view all client results in a data collection format suitable for export and statistical analysis.
                  </p>
                </EmptyStateContainer>
              </TableCard>
            )}
          </>
        )}
        {renderResultDetails()}
      </Container>
    </PageWrapper>
  );
};

export default Results;
